import { type Face, PLAIN_FACES } from '../mahjong-solitaire/tiles.js';

/**
 * Tile helpers for the four-player table game. The faces are the solitaire engine's (`mahjong-solitaire/tiles.ts`): 34 kinds with four copies
 * each (dots, bamboo and characters 1-9, four winds, three dragons), plus, when the flowers option is on, 4 flowers and 4 seasons as bonus tiles.
 */
/** The 34 playable kinds in a fixed order: dots 1-9, bamboo 1-9, characters 1-9, winds, dragons. A kind's index is its position here. */
export const TILE_KINDS: readonly Face[] = PLAIN_FACES;
export const KIND_COUNT = TILE_KINDS.length;
const INDEX = new Map<Face, number>(TILE_KINDS.map((f, i) => [f, i]));

export type MahjongSuit = 'dots' | 'bamboo' | 'characters';

/** The kind index of a face, or -1 for a bonus tile (flower or season). */
export const kindIndex = (face: Face): number => INDEX.get(face) ?? -1;
export const faceOfKind = (i: number): Face => TILE_KINDS[i] as Face;
export const isBonusFace = (face: Face): boolean => face.startsWith('flower-') || face.startsWith('season-');
export const isWind = (face: Face): boolean => face.startsWith('wind-');
export const isDragon = (face: Face): boolean => face.startsWith('dragon-');
export const isHonour = (face: Face): boolean => isWind(face) || isDragon(face);

export function suitOf(face: Face): MahjongSuit | null {
  const s = face.split('-')[0];
  return s === 'dots' || s === 'bamboo' || s === 'characters' ? s : null;
}
/** 1 to 9 for a suited tile, 0 for anything else. */
export const rankOf = (face: Face): number => (suitOf(face) ? Number(face.split('-')[1]) : 0);

/** Hands are kept in kind order, bonus tiles last, so a hand always reads the same way. */
export const faceOrder = (face: Face): number => {
  const i = kindIndex(face);
  return i >= 0 ? i : 100 + Number(face.split('-')[1]);
};
export const sortFaces = (faces: readonly Face[]): Face[] => [...faces].sort((a, b) => faceOrder(a) - faceOrder(b));

/** How many of each kind (bonus tiles are not counted). */
export function toCounts(faces: readonly Face[]): number[] {
  const c = new Array<number>(KIND_COUNT).fill(0);
  for (const f of faces) {
    const i = kindIndex(f);
    if (i >= 0) c[i] = (c[i] as number) + 1;
  }
  return c;
}

/** A set in a winning hand: three in a row of one suit (chow) or three of a kind (pung; a kong counts as a pung). */
export interface MahjongSet {
  readonly kind: 'chow' | 'pung';
  readonly tiles: readonly Face[];
}
export interface Decomposition {
  readonly sets: readonly MahjongSet[];
  readonly pair: Face;
}

/** Can the three kinds starting at `i` form a chow: same suit, consecutive, no wrap round the suit's end. */
const chowAt = (i: number): boolean => i < 27 && i % 9 <= 6;

/**
 * Every way to split `faces` into exactly `setsNeeded` sets and one pair. Empty when the hand is not complete. A complete hand needs
 * 3 * setsNeeded + 2 tiles; some hands split more than one way (for example 111222333 is three chows or three pungs) and the scorer picks the best.
 */
export function decompositions(faces: readonly Face[], setsNeeded: number): Decomposition[] {
  const c = toCounts(faces);
  if (c.reduce((a, b) => a + b, 0) !== setsNeeded * 3 + 2 || faces.some(isBonusFace)) return [];
  const out: Decomposition[] = [];
  const sets: MahjongSet[] = [];
  const fill = (pair: number): void => {
    let i = 0;
    while (i < KIND_COUNT && c[i] === 0) i++;
    if (i === KIND_COUNT) {
      if (sets.length === setsNeeded) out.push({ sets: [...sets], pair: faceOfKind(pair) });
      return;
    }
    if (sets.length >= setsNeeded) return;
    if ((c[i] as number) >= 3) {
      c[i] = (c[i] as number) - 3;
      sets.push({ kind: 'pung', tiles: [faceOfKind(i), faceOfKind(i), faceOfKind(i)] });
      fill(pair);
      sets.pop();
      c[i] = (c[i] as number) + 3;
    }
    if (chowAt(i) && (c[i + 1] as number) > 0 && (c[i + 2] as number) > 0) {
      c[i] = (c[i] as number) - 1;
      c[i + 1] = (c[i + 1] as number) - 1;
      c[i + 2] = (c[i + 2] as number) - 1;
      sets.push({ kind: 'chow', tiles: [faceOfKind(i), faceOfKind(i + 1), faceOfKind(i + 2)] });
      fill(pair);
      sets.pop();
      c[i] = (c[i] as number) + 1;
      c[i + 1] = (c[i + 1] as number) + 1;
      c[i + 2] = (c[i + 2] as number) + 1;
    }
  };
  for (let p = 0; p < KIND_COUNT; p++) {
    if ((c[p] as number) < 2) continue;
    c[p] = (c[p] as number) - 2;
    fill(p);
    c[p] = (c[p] as number) + 2;
  }
  return out;
}

/** Is this hand (plus `setsNeeded` sets already melded) complete: four sets and a pair? */
export const isCompleteHand = (faces: readonly Face[], setsNeeded: number): boolean => decompositions(faces, setsNeeded).length > 0;

/**
 * How many tiles away from a complete hand, as the usual "shanten" count: -1 is complete, 0 is one tile from complete (ready), and so on.
 * `counts` is the concealed hand by kind, `exposed` the number of sets already melded. Regular hands only (four sets and a pair).
 */
export function shanten(counts: number[], exposed: number): number {
  let best = 8;
  const go = (start: number, m: number, t: number, p: number): void => {
    let i = start;
    while (i < KIND_COUNT && counts[i] === 0) i++;
    if (i >= KIND_COUNT) {
      const partials = Math.min(t, 4 - m);
      best = Math.min(best, 8 - 2 * m - partials - p);
      return;
    }
    if (best <= -1) return;
    const c = counts;
    if ((c[i] as number) >= 3) {
      c[i] = (c[i] as number) - 3;
      go(i, m + 1, t, p);
      c[i] = (c[i] as number) + 3;
    }
    if (chowAt(i) && (c[i + 1] as number) > 0 && (c[i + 2] as number) > 0) {
      c[i] = (c[i] as number) - 1;
      c[i + 1] = (c[i + 1] as number) - 1;
      c[i + 2] = (c[i + 2] as number) - 1;
      go(i, m + 1, t, p);
      c[i] = (c[i] as number) + 1;
      c[i + 1] = (c[i + 1] as number) + 1;
      c[i + 2] = (c[i + 2] as number) + 1;
    }
    if ((c[i] as number) >= 2) {
      c[i] = (c[i] as number) - 2;
      if (p === 0) go(i, m, t, 1);
      go(i, m, t + 1, p);
      c[i] = (c[i] as number) + 2;
    }
    if (i < 27 && i % 9 <= 7 && (c[i + 1] as number) > 0) {
      c[i] = (c[i] as number) - 1;
      c[i + 1] = (c[i + 1] as number) - 1;
      go(i, m, t + 1, p);
      c[i] = (c[i] as number) + 1;
      c[i + 1] = (c[i + 1] as number) + 1;
    }
    if (i < 27 && i % 9 <= 6 && (c[i + 2] as number) > 0) {
      c[i] = (c[i] as number) - 1;
      c[i + 2] = (c[i + 2] as number) - 1;
      go(i, m, t + 1, p);
      c[i] = (c[i] as number) + 1;
      c[i + 2] = (c[i + 2] as number) + 1;
    }
    c[i] = (c[i] as number) - 1; // leave this tile on its own
    go(i, m, t, p);
    c[i] = (c[i] as number) + 1;
  };
  go(0, exposed, 0, 0);
  return best;
}

/** The pairs of tiles in `hand` that make a chow with `tile`: (t-2,t-1), (t-1,t+1) or (t+1,t+2), same suit. */
export function chowPairs(hand: readonly Face[], tile: Face): [Face, Face][] {
  const suit = suitOf(tile);
  if (!suit) return [];
  const r = rankOf(tile);
  const has = (rank: number) => rank >= 1 && rank <= 9 && hand.includes(`${suit}-${rank}`);
  const out: [Face, Face][] = [];
  for (const [a, b] of [[r - 2, r - 1], [r - 1, r + 1], [r + 1, r + 2]] as const) {
    if (has(a) && has(b)) out.push([`${suit}-${a}`, `${suit}-${b}`]);
  }
  return out;
}
